import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useRoomStore } from '@/stores'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { MessageSquare } from 'lucide-react'
import { RoomOpen, RoomClose } from '@core/models'

const RoomList = observer(() => {
  const { tenantId } = useParams<{ tenantId: string }>()
  const navigate = useNavigate()
  const roomStore = useRoomStore()

  useEffect(() => {
    if (tenantId) {
      roomStore.getRooms(tenantId)
    }
  }, [tenantId, roomStore])

  const handleViewDetails = (roomId: string) => {
    navigate(`/tenants/${tenantId}/rooms/${roomId}`)
  }

  const getStateLabel = (state: number) => {
    switch (state) {
      case RoomOpen:
        return 'オープン'
      case RoomClose:
        return 'クローズ'
      default:
        return '-'
    }
  }

  return (
    <>
      <div className="space-y-6 mb-6">
        <h1 className="text-3xl font-bold">ルーム管理</h1>
      </div>
      <Card>
        <CardContent>
          {roomStore.roomsArray.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>ルームがありません</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>ルーム名</TableHead>
                  <TableHead>状態</TableHead>
                  <TableHead>作成日時</TableHead>
                  <TableHead>更新日時</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roomStore.roomsArray.map((room) => (
                  <TableRow key={room.id}>
                    <TableCell className="font-mono text-sm">
                      {room.id}
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        {room.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={room.state === RoomOpen ? 'default' : 'secondary'}
                      >
                        {getStateLabel(room.state)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {room.created 
                        ? new Date(room.created).toLocaleString('ja-JP')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {room.updated 
                        ? new Date(room.updated).toLocaleString('ja-JP')
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(room.id)}
                      >
                        詳細
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  )
})

export default RoomList